import {
  differenceInCalendarDays,
  endOfDay,
  endOfQuarter,
  endOfToday,
  startOfDay,
  startOfQuarter,
  subDays,
} from 'date-fns';

import {
  getEmployeeChecksFeed,
  getEmployeeOrderItems,
  getEmployeeTopCheck,
  getEmployeeTopDish,
  getPersonalOrdersSummary,
} from '@/services/employee-analytics.service';
import { getGoodsRead } from '@/services/goods.service';
import { getPointsName } from '@/services/points.service';
import {
  buildCheckDetails,
  buildEmployeeDetails,
  buildEmployeeRatingRows,
  buildEmployeeSummary,
  buildEmployeeChecks,
} from '@/views/statistic/employeeAnalytics/calculators';
import {
  filterRecordsByDateRange,
  filterRecordsByEmployeeSearch,
  filterRecordsByPoints,
  isPaidDineInRecord,
} from '@/views/statistic/employeeAnalytics/filters';
import {
  buildReferenceMaps,
  normalizeEmployee,
  normalizeOrderItemsPayload,
  normalizePoint,
} from '@/views/statistic/employeeAnalytics/normalizers';
import {
  normalizePersonalOrdersSummary,
  toEmployeeAnalyticsSummary,
} from '@/views/statistic/employeeAnalytics/summaryAdapter';
import { getAuthStoredValue } from '@/utils/storage.utils';

const inflightSummaryRequests = new Map();
const inflightFeedRequests = new Map();
const inflightTopCheckRequests = new Map();
const inflightTopDishRequests = new Map();
const inflightEmployeeAggregateRequests = new Map();
let recordsLoadSequence = 0;
const ORDER_SORT_FIELDS = ['createdAt', 'resultSum', 'rating', 'tableId', 'number'];
const ORDER_SORT_DIRECTIONS = ['asc', 'desc'];
const DEFAULT_CHECKS_SORT = { field: 'createdAt', order: 'desc' };
const REFERENCE_DATA_TIMEOUT_MS = 5000;
const DEFAULT_CHECKS_SORT_DIRECTION_BY_FIELD = {
  createdAt: 'desc',
  resultSum: 'desc',
  rating: 'desc',
  tableId: 'asc',
  number: 'desc',
};
const CHECK_SORT_ROW_FIELD_MAP = {
  createdAt: 'paidAt',
  resultSum: 'netAmount',
  rating: 'guestRating',
  tableId: 'tableNumber',
  number: 'checkNumber',
};

function withTimeout(promise, timeoutMs, fallback) {
  let timerId;
  const timeoutPromise = new Promise((resolve) => {
    timerId = setTimeout(() => {
      resolve(fallback);
    }, timeoutMs);
  });

  return Promise.race([promise, timeoutPromise]).finally(() => {
    clearTimeout(timerId);
  });
}

function normalizeReferenceList(value) {
  return Array.isArray(value) ? value : [];
}

function normalizeNonEmptyString(value) {
  const normalized = String(value ?? '').trim();
  return normalized || '';
}

function hasNestedOrderModifiers(item = {}) {
  return ['modifiers', 'modificators', 'modifications', 'productModifiers', 'orderItemModifiers'].some((key) =>
    Array.isArray(item?.[key]) && item[key].length,
  );
}

function collectGoodsDetailsProductIds(orderItems = []) {
  const items = Array.isArray(orderItems) ? orderItems : [];
  const parentIdsWithModifiers = new Set(
    items.map((item) => normalizeNonEmptyString(item?.itemId)).filter(Boolean),
  );

  return [
    ...new Set(
      items
        .filter((item) => {
          const itemId = normalizeNonEmptyString(item?.id);
          const isChildModifier = Boolean(normalizeNonEmptyString(item?.itemId));
          return (
            !isChildModifier &&
            (normalizeNonEmptyString(item?.sizeId) || parentIdsWithModifiers.has(itemId) || hasNestedOrderModifiers(item))
          );
        })
        .map((item) => normalizeNonEmptyString(item?.productId))
        .filter(Boolean),
    ),
  ];
}

function findSelectedSize(goods = [], productId = '', sizeId = '') {
  const product = (Array.isArray(goods) ? goods : []).find((item) => normalizeNonEmptyString(item?.id) === productId);
  const sizes = Array.isArray(product?.sizes) ? product.sizes : [];
  return sizes.find((size) => normalizeNonEmptyString(size?.id) === sizeId) || null;
}

function getProductCatalogModifiers(product = {}) {
  const directModifiers = Array.isArray(product?.modifiers) ? product.modifiers : [];
  const groupModifiers = Array.isArray(product?.groupModifiers)
    ? product.groupModifiers.flatMap((group) => (Array.isArray(group?.modifiers) ? group.modifiers : []))
    : [];

  return [...directModifiers, ...groupModifiers];
}

function findCatalogModifier(product = {}, modifier = {}) {
  const modifiers = getProductCatalogModifiers(product);
  const modifierProductId = normalizeNonEmptyString(modifier?.productId);
  const modifierId = normalizeNonEmptyString(modifier?.id);
  const modifierName = normalizeNonEmptyString(modifier?.productName ?? modifier?.name).toLowerCase();

  return (
    modifiers.find((item) => normalizeNonEmptyString(item?.id) === modifierProductId) ||
    modifiers.find((item) => normalizeNonEmptyString(item?.id) === modifierId) ||
    modifiers.find((item) => normalizeNonEmptyString(item?.name).toLowerCase() === modifierName) ||
    null
  );
}

function enrichModifiersWithCatalogItems(item = {}, product = {}) {
  const modifierKeys = ['modifiers', 'modificators', 'modifications', 'productModifiers', 'orderItemModifiers'];
  return modifierKeys.reduce((acc, key) => {
    if (!Array.isArray(acc?.[key])) return acc;

    return {
      ...acc,
      [key]: acc[key].map((modifier) => {
        const selectedCatalogItem = findCatalogModifier(product, modifier);
        return selectedCatalogItem ? { ...modifier, selectedCatalogItem } : modifier;
      }),
    };
  }, item);
}

async function enrichOrderItemsWithGoodsDetails({ orderItems = [], pointId = '' } = {}) {
  const items = Array.isArray(orderItems) ? orderItems : [];
  const normalizedPointId = normalizeNonEmptyString(pointId);
  const productIds = collectGoodsDetailsProductIds(items);

  if (!items.length || !normalizedPointId || !productIds.length) return items;

  try {
    const response = await getGoodsRead({
      pointId: normalizedPointId,
      productIds,
    });
    const goods = Array.isArray(response?.data) ? response.data : [];
    const goodsById = goods.reduce((acc, item) => {
      const id = normalizeNonEmptyString(item?.id);
      if (id) acc[id] = item;
      return acc;
    }, {});
    const orderItemsById = items.reduce((acc, item) => {
      const id = normalizeNonEmptyString(item?.id);
      if (id) acc[id] = item;
      return acc;
    }, {});

    return items.map((item) => {
      const sizeId = normalizeNonEmptyString(item?.sizeId);
      const productId = normalizeNonEmptyString(item?.productId);
      const parentId = normalizeNonEmptyString(item?.itemId);
      const parentProductId = normalizeNonEmptyString(orderItemsById[parentId]?.productId);
      const parentProduct = parentProductId ? goodsById[parentProductId] : null;

      if (parentProduct) {
        const selectedCatalogItem = findCatalogModifier(parentProduct, item);
        return selectedCatalogItem ? { ...item, selectedCatalogItem } : item;
      }

      if (!sizeId || !productId) return item;

      const product = goodsById[productId];
      const selectedSize = findSelectedSize(goods, productId, sizeId);
      const withSize = selectedSize ? { ...item, selectedSize } : item;
      return product ? enrichModifiersWithCatalogItems(withSize, product) : withSize;
    });
  } catch (error) {
    console.error('employeeAnalytics:loadSelectedCheckItems:sizes:error', error);
    return items;
  }
}

function parseStoredJson(value) {
  if (!value || typeof value !== 'string') return null;

  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function firstNonEmpty(values = []) {
  return values.map((value) => String(value || '').trim()).find(Boolean) || '';
}

function getSafeAuthStoredValue(key) {
  if (typeof localStorage === 'undefined' || typeof sessionStorage === 'undefined') return null;
  return getAuthStoredValue(key);
}

function getCurrentAuthContext(rootState = {}) {
  const loginState = rootState?.login || {};
  const storedUser = parseStoredJson(getSafeAuthStoredValue('user'));
  const user = loginState.user || storedUser || {};
  const merchant = loginState.merchant || {};
  const uid = firstNonEmpty([
    loginState.userid,
    getSafeAuthStoredValue('uid'),
    user?.uid,
    user?.userId,
    user?.id,
  ]);
  const userName = firstNonEmpty([
    user?.fullName,
    user?.employeeName,
    `${user?.firstName || user?.firstname || ''} ${user?.lastName || user?.lastname || ''}`,
    user?.name,
    user?.email,
  ]);
  const merchantName = firstNonEmpty([
    merchant?.merchantName,
    merchant?.name,
    merchant?.title,
    merchant?.merchantSlugName,
  ]);

  return {
    uid,
    user,
    merchant,
    displayName: firstNonEmpty([userName, merchantName, uid]),
  };
}

function buildCurrentOperatorEmployee(rootState = {}) {
  const context = getCurrentAuthContext(rootState);
  if (!context.uid) return null;

  return normalizeEmployee({
    ...context.user,
    id: context.uid,
    employeeId: context.uid,
    personalId: context.uid,
    fullName: context.displayName,
    employeeName: context.displayName,
    point: context.user?.point,
    points: context.user?.points,
    pointId: context.user?.pointId,
    pointIds: context.user?.pointIds,
    pointTitle: context.user?.pointTitle,
    pointTitles: context.user?.pointTitles,
  });
}

function isReferenceDebugEnabled() {
  return typeof window !== 'undefined' && window.localStorage?.getItem('employeeAnalyticsRefsDebug') === '1';
}

function debugReferenceData({ pointsRaw = [], personalsRaw = [], points = [], employees = [], currentOperator = null } = {}) {
  if (!isReferenceDebugEnabled()) return;

  console.groupCollapsed('[employeeAnalyticsRefs] reference data');
  console.log('pointsRawSample', normalizeReferenceList(pointsRaw).slice(0, 10));
  console.log('personalsRawSample', normalizeReferenceList(personalsRaw).slice(0, 10));
  console.log('currentOperator', currentOperator);
  console.table(
    employees.slice(0, 20).map((employee) => ({
      id: employee.employeeId,
      externalId: employee.employeeExternalId,
      name: employee.employeeName,
      pointIds: employee.pointIds.join(', '),
      pointTitles: employee.pointTitles.join(', '),
    })),
  );
  console.table(
    points.slice(0, 20).map((point) => ({
      id: point.id,
      title: point.title,
    })),
  );
  console.groupEnd();
}

function isEmployeeAnalyticsDebugEnabled() {
  return typeof window !== 'undefined' && window.localStorage?.getItem('employeeAnalyticsDebug') === '1';
}

function debugEmployeeAnalyticsLoad(label, payload = {}) {
  if (!isEmployeeAnalyticsDebugEnabled()) return;

  console.groupCollapsed(`[employeeAnalytics] ${label}`);
  console.log(payload);
  console.groupEnd();
}

function normalizeCheckSortField(value, fallback = DEFAULT_CHECKS_SORT.field) {
  return ORDER_SORT_FIELDS.includes(value) ? value : fallback;
}

function normalizeCheckSortOrder(value, fallback = DEFAULT_CHECKS_SORT.order) {
  return ORDER_SORT_DIRECTIONS.includes(value) ? value : fallback;
}

function normalizeChecksSort(value = {}) {
  const field = normalizeCheckSortField(value?.field);
  return {
    field,
    order: normalizeCheckSortOrder(value?.order, DEFAULT_CHECKS_SORT_DIRECTION_BY_FIELD[field]),
  };
}

function mapChecksSortToRowSort(sort = DEFAULT_CHECKS_SORT) {
  const normalized = normalizeChecksSort(sort);
  return {
    field: CHECK_SORT_ROW_FIELD_MAP[normalized.field] || CHECK_SORT_ROW_FIELD_MAP.createdAt,
    order: normalized.order,
  };
}

function toUnixSeconds(value) {
  const normalized = Number(value);
  if (!Number.isFinite(normalized) || normalized <= 0) return 0;
  return normalized > 9999999999 ? Math.floor(normalized / 1000) : Math.floor(normalized);
}

function toUnixMilliseconds(value) {
  const seconds = toUnixSeconds(value);
  return seconds ? seconds * 1000 : 0;
}

function normalizeDateRangeSeconds(range = []) {
  if (!Array.isArray(range) || range.length !== 2) return [];

  const from = toUnixSeconds(range[0]);
  const to = toUnixSeconds(range[1]);
  return from && to ? [from, to] : [null, null];
}

function getPresetRange(preset = '7d') {
  const now = new Date();

  switch (preset) {
    case '30d':
      return [toUnixSeconds(startOfDay(subDays(now, 29)).getTime()), toUnixSeconds(endOfToday().getTime())];
    case 'quarter':
      return [toUnixSeconds(startOfQuarter(now).getTime()), toUnixSeconds(endOfQuarter(now).getTime())];
    case 'custom':
      return [null, null];
    case '7d':
    default:
      return [toUnixSeconds(startOfDay(subDays(now, 6)).getTime()), toUnixSeconds(endOfToday().getTime())];
  }
}

function defaultState() {
  return {
    records: [],
    recordsRequestKey: '',
    recordsLoaded: false,
    recordsLoading: false,
    recordsLoadingCount: 0,
    recordsLoadingRequestId: 0,
    recordsError: null,
    lastFetchedAt: null,
    summaryLoading: false,
    summaryLoadingRequestId: 0,
    summarySnapshot: null,
    ratingRowsSnapshot: [],
    summaryCache: {},
    topCheckCache: {},
    topDishCache: {},
    selectedEmployeeAggregateKey: '',
    selectedEmployeeAggregateRecords: [],
    selectedEmployeeCheckSearchQuery: '',

    points: [],
    pointsLoaded: false,
    refsMerchantId: null,
    employeesById: {},
    pointsById: {},
    reviewsByCheckId: {},

    selectedPointIds: [],
    datePreset: '7d',
    dateRange: getPresetRange('7d'),
    searchEmployee: '',
    employeeSort: { field: 'revenue', order: 'desc' },
    checksSort: { ...DEFAULT_CHECKS_SORT },
    checksPagination: { page: 1, rowsPerPage: 10 },
    checksTotal: 0,

    selectedEmployeeId: null,
    selectedEmployeeTopCheck: null,
    selectedEmployeeTopCheckKey: '',
    selectedEmployeeTopCheckLoading: false,
    selectedEmployeeTopCheckLoadingKey: '',
    selectedEmployeeTopDish: null,
    selectedEmployeeTopDishKey: '',
    selectedEmployeeTopDishLoading: false,
    selectedEmployeeTopDishLoadingKey: '',
    selectedCheckId: null,
    selectedCheckRecord: null,
    selectedCheckItemsLoading: false,

    exportLoading: false,
    hydratedFromRoute: false,
  };
}

function sortRows(rows = [], sort = { field: 'revenue', order: 'desc' }) {
  const field = sort?.field || 'revenue';
  const order = sort?.order === 'asc' ? 1 : -1;

  return [...rows].sort((left, right) => {
    const leftValue = left?.[field];
    const rightValue = right?.[field];

    if (typeof leftValue === 'string' || typeof rightValue === 'string') {
      return String(leftValue || '').localeCompare(String(rightValue || '')) * order;
    }

    return ((Number(leftValue) || 0) - (Number(rightValue) || 0)) * order;
  });
}

function buildRequestKey({
  merchantId = 'merchant',
  employeeId = '',
  pointIds = [],
  dateRange = [],
  scope = 'paid-dine-in',
  checksSort = DEFAULT_CHECKS_SORT,
  checksPagination = null,
  orderIds = [],
  searchQuery = '',
} = {}) {
  const [from, to] = dateRange;
  const normalizedChecksSort = normalizeChecksSort(checksSort);
  const page = checksPagination ? Number(checksPagination.page || 1) : '';
  const rowsPerPage = checksPagination ? Number(checksPagination.rowsPerPage || 10) : '';
  const orderIdsKey = Array.isArray(orderIds) ? [...orderIds].map(String).sort().join(',') : '';
  const searchKey = normalizeCheckSearchQuery(searchQuery);
  return [
    merchantId,
    employeeId || '',
    [...pointIds].sort().join(','),
    from || '',
    to || '',
    scope,
    normalizedChecksSort.field,
    normalizedChecksSort.order,
    page,
    rowsPerPage,
    orderIdsKey,
    searchKey,
  ].join('|');
}

function buildSummaryKey({ merchantId = 'merchant', pointIds = [], dateRange = [], statuses = [] } = {}) {
  const [from, to] = dateRange;
  const statusesKey = Array.isArray(statuses) ? [...statuses].map(String).sort().join(',') : '';
  return [merchantId, [...pointIds].sort().join(','), from || '', to || '', statusesKey].join('|');
}

function buildEmployeeAggregateKey({
  merchantId = 'merchant',
  employeeId = '',
  pointIds = [],
  dateRange = [],
} = {}) {
  return buildRequestKey({
    merchantId,
    employeeId,
    pointIds,
    dateRange,
    scope: 'employee-aggregate',
    checksSort: DEFAULT_CHECKS_SORT,
  });
}

function hasSummaryData(entry = null) {
  return Boolean(entry?.summary || (Array.isArray(entry?.rows) && entry.rows.length));
}

function hasTopDishData(entry = null) {
  return Boolean(entry && entry.topDish);
}

function hasTopCheckData(entry = null) {
  return Boolean(entry && Object.prototype.hasOwnProperty.call(entry, 'topCheck'));
}

function getPreviousAnalogPeriod(dateRange = []) {
  if (!Array.isArray(dateRange) || dateRange.length !== 2) return [];
  const [fromRaw, toRaw] = dateRange;
  if (!fromRaw || !toRaw) return [];

  const from = startOfDay(new Date(toUnixMilliseconds(fromRaw)));
  const to = startOfDay(new Date(toUnixMilliseconds(toRaw)));
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return [];

  const days = differenceInCalendarDays(to, from) + 1;
  if (!Number.isFinite(days) || days <= 0) return [];

  const prevTo = endOfDay(subDays(from, 1)).getTime();
  const prevFrom = startOfDay(subDays(from, days)).getTime();

  return [toUnixSeconds(prevFrom), toUnixSeconds(prevTo)];
}

function resolveEmployeeId(employeeId) {
  const key = employeeId == null ? '' : String(employeeId);
  if (!key) return '';
  return key;
}

function fetchPersonalOrdersSummaryDedup(key, request = {}, options = {}) {
  if (!key) return getPersonalOrdersSummary(request);
  const force = Boolean(options?.force);
  const inflight = inflightSummaryRequests.get(key);
  if (inflight && !force) return inflight;

  const promise = Promise.resolve()
    .then(() => getPersonalOrdersSummary(request))
    .finally(() => {
      if (inflightSummaryRequests.get(key) === promise) {
        inflightSummaryRequests.delete(key);
      }
    });

  inflightSummaryRequests.set(key, promise);
  return promise;
}

function fetchEmployeeTopDishDedup(key, request = {}, options = {}) {
  if (!key) return getEmployeeTopDish(request);
  const force = Boolean(options?.force);
  const inflight = inflightTopDishRequests.get(key);
  if (inflight && !force) return inflight;

  const promise = Promise.resolve()
    .then(() => getEmployeeTopDish(request))
    .finally(() => {
      if (inflightTopDishRequests.get(key) === promise) {
        inflightTopDishRequests.delete(key);
      }
    });

  inflightTopDishRequests.set(key, promise);
  return promise;
}

function fetchEmployeeTopCheckDedup(key, request = {}, options = {}) {
  if (!key) return getEmployeeTopCheck(request);
  const force = Boolean(options?.force);
  const inflight = inflightTopCheckRequests.get(key);
  if (inflight && !force) return inflight;

  const promise = Promise.resolve()
    .then(() => getEmployeeTopCheck(request))
    .finally(() => {
      if (inflightTopCheckRequests.get(key) === promise) {
        inflightTopCheckRequests.delete(key);
      }
    });

  inflightTopCheckRequests.set(key, promise);
  return promise;
}

function fetchEmployeeAggregateDedup(key, request = {}) {
  if (!key) return getEmployeeChecksFeed(request);
  const inflight = inflightEmployeeAggregateRequests.get(key);
  if (inflight) return inflight;

  const promise = Promise.resolve()
    .then(() => getEmployeeChecksFeed(request))
    .finally(() => {
      inflightEmployeeAggregateRequests.delete(key);
    });

  inflightEmployeeAggregateRequests.set(key, promise);
  return promise;
}

function hasEmployeeSummaryRow(rows = [], employeeId = '') {
  const key = String(employeeId || '');
  if (!key || !Array.isArray(rows)) return false;
  return rows.some((row) => String(row?.employeeId ?? row?.personalId ?? '') === key);
}

function filterScopedRecords(records = [], state = {}) {
  const scoped = records.filter(isPaidDineInRecord);
  const byDate = filterRecordsByDateRange(scoped, state.dateRange?.[0], state.dateRange?.[1]);
  return filterRecordsByPoints(byDate, state.selectedPointIds);
}

function normalizeCheckSearchQuery(value = '') {
  return String(value || '').trim().toLowerCase();
}

function collectOrderIdsByNumber(records = [], query = '') {
  const normalizedQuery = normalizeCheckSearchQuery(query);
  if (!normalizedQuery) return [];

  return filterRecordsByCheckNumber(records, normalizedQuery)
    .map((record) => String(record?.checkId ?? record?.id ?? record?.raw?.id ?? ''))
    .filter(Boolean);
}

function filterRecordsByCheckNumber(records = [], query = '') {
  const normalizedQuery = normalizeCheckSearchQuery(query);
  if (!normalizedQuery) return records;

  return records.filter((record) =>
    String(record?.checkNumber ?? record?.number ?? record?.raw?.number ?? '')
      .toLowerCase()
      .includes(normalizedQuery),
  );
}

function computeTrendPercent(currentValue, prevValue) {
  const current = Number(currentValue);
  const prev = Number(prevValue);
  if (!Number.isFinite(current) || !Number.isFinite(prev)) return { trendPercent: null, trendDirection: null };

  if (prev === 0) {
    if (current === 0) {
      return { trendPercent: 0, trendDirection: 'flat' };
    }
    return { trendPercent: null, trendDirection: null };
  }

  const raw = ((current - prev) / prev) * 100;
  if (!Number.isFinite(raw)) return { trendPercent: null, trendDirection: null };

  const trendPercent = Math.round(raw);
  const trendDirection = trendPercent > 0 ? 'up' : trendPercent < 0 ? 'down' : 'flat';
  return { trendPercent, trendDirection };
}

function applySummaryComparison(currentSummary, prevSummary) {
  if (!currentSummary || typeof currentSummary !== 'object') return null;
  if (!prevSummary || typeof prevSummary !== 'object') {
    return {
      revenue: { ...currentSummary.revenue, trendPercent: null, trendDirection: null },
      checksCount: { ...currentSummary.checksCount, trendPercent: null, trendDirection: null },
      averageCheck: { ...currentSummary.averageCheck, trendPercent: null, trendDirection: null },
      bonuses: { ...currentSummary.bonuses, trendPercent: null, trendDirection: null },
      guestRating: { ...currentSummary.guestRating, trendPercent: null, trendDirection: null },
    };
  }

  const keys = ['revenue', 'checksCount', 'averageCheck', 'bonuses', 'guestRating'];
  const next = { ...currentSummary };

  for (const key of keys) {
    const currentMetric = currentSummary?.[key];
    const prevMetric = prevSummary?.[key];

    if (!currentMetric || !prevMetric) {
      next[key] = { ...(currentMetric || {}), trendPercent: null, trendDirection: null };
      continue;
    }

    const { trendPercent, trendDirection } = computeTrendPercent(currentMetric.value, prevMetric.value);
    next[key] = { ...currentMetric, trendPercent, trendDirection };
  }

  return next;
}

export default {
  namespaced: true,
  state: defaultState,
  getters: {
    employeeAnalyticsRecords: (state) => state.records,
    employeeAnalyticsLoading: (state) => state.recordsLoading,
    employeeAnalyticsError: (state) => state.recordsError,
    selectedEmployeeSummaryLoading: (state) => state.summaryLoading,
    employeeAnalyticsFilters: (state) => ({
      selectedPointIds: state.selectedPointIds,
      datePreset: state.datePreset,
      dateRange: state.dateRange,
      searchEmployee: state.searchEmployee,
      employeeSort: state.employeeSort,
      checksSort: state.checksSort,
      checksPagination: state.checksPagination,
    }),
    employeeAnalyticsSelectedEmployeeId: (state) => state.selectedEmployeeId,
    employeeAnalyticsSelectedCheckId: (state) => state.selectedCheckId,
    selectedEmployeeTopCheckLoading: (state) => state.selectedEmployeeTopCheckLoading,
    selectedEmployeeTopDishLoading: (state) => state.selectedEmployeeTopDishLoading,
    selectedCheckItemsLoading: (state) => state.selectedCheckItemsLoading,
    filteredEmployeeAnalyticsRecords: (state) => {
      return filterScopedRecords(state.records, state);
    },
    employeeAnalyticsDashboardCards: (state, getters) => {
      if (state.summarySnapshot) return state.summarySnapshot;
      return normalizePersonalOrdersSummary(buildEmployeeSummary(getters.filteredEmployeeAnalyticsRecords));
    },
    employeeRatingRows: (state, getters) => {
      const rows = state.ratingRowsSnapshot.length
        ? state.ratingRowsSnapshot
        : buildEmployeeRatingRows(getters.filteredEmployeeAnalyticsRecords);
      const searched = state.ratingRowsSnapshot.length
        ? rows.filter((row) => {
            const searchValue = String(state.searchEmployee || '')
              .trim()
              .toLowerCase();
            if (!searchValue) return true;

            return [row.employeeName]
              .filter(Boolean)
              .some((value) => String(value).toLowerCase().includes(searchValue));
          })
        : (() => {
            const matched = filterRecordsByEmployeeSearch(
              getters.filteredEmployeeAnalyticsRecords,
              state.searchEmployee,
            );
            const searchedIds = new Set(matched.map((record) => record.employeeId));
            return rows.filter((row) => (state.searchEmployee ? searchedIds.has(row.employeeId) : true));
          })();

      return sortRows(searched, state.employeeSort);
    },
    selectedEmployeeSummary: (state, getters, rootState) => {
      const merchantId = rootState?.login?.merchant?.merchantId || rootState?.login?.merchant?.id || 'merchant';
      const selectedEmployeeId = resolveEmployeeId(state.selectedEmployeeId, state.employeesById);
      const aggregateKey = selectedEmployeeId
        ? buildEmployeeAggregateKey({
            merchantId,
            employeeId: selectedEmployeeId,
            pointIds: state.selectedPointIds,
            dateRange: state.dateRange,
          })
        : '';
      const activeCheckSearchQuery = normalizeCheckSearchQuery(state.selectedEmployeeCheckSearchQuery);
      const hasAggregateRecords = aggregateKey && state.selectedEmployeeAggregateKey === aggregateKey;
      const aggregateRecords = hasAggregateRecords
        ? filterScopedRecords(state.selectedEmployeeAggregateRecords, state)
        : [];
      const summaryRecords = hasAggregateRecords
        ? filterRecordsByCheckNumber(aggregateRecords, activeCheckSearchQuery)
        : filterRecordsByCheckNumber(getters.filteredEmployeeAnalyticsRecords, activeCheckSearchQuery);
      const summaryUsesSearch = Boolean(activeCheckSearchQuery);
      const details = buildEmployeeDetails(
        summaryRecords,
        selectedEmployeeId,
        state.ratingRowsSnapshot,
        state.employeesById,
        {
          selectedPointIds: state.selectedPointIds,
          preferRecordsSummary: hasAggregateRecords,
          checksTotal: state.recordsLoaded && (Number(state.checksTotal) > 0 || state.records.length === 0)
            ? state.checksTotal
            : undefined,
        },
      );
      const topCheckKey = selectedEmployeeId
        ? buildRequestKey({
            merchantId,
            employeeId: selectedEmployeeId,
            pointIds: state.selectedPointIds,
            dateRange: state.dateRange,
            scope: 'top-check',
          })
        : '';
      const topDishKey = selectedEmployeeId
        ? buildRequestKey({
            merchantId,
            employeeId: selectedEmployeeId,
            pointIds: state.selectedPointIds,
            dateRange: state.dateRange,
            scope: 'top-dish',
          })
        : '';

      return {
        ...details,
        ...(!summaryUsesSearch && topCheckKey && state.selectedEmployeeTopCheckKey === topCheckKey && state.selectedEmployeeTopCheck
          ? { topCheck: state.selectedEmployeeTopCheck }
          : {}),
        ...(!summaryUsesSearch && topDishKey && state.selectedEmployeeTopDishKey === topDishKey && state.selectedEmployeeTopDish
          ? { topDish: state.selectedEmployeeTopDish }
          : {}),
      };
    },
    selectedEmployeeChecks: (state, getters) => {
      const checks = buildEmployeeChecks(getters.filteredEmployeeAnalyticsRecords, state.selectedEmployeeId);
      return sortRows(checks, mapChecksSortToRowSort(state.checksSort));
    },
    selectedEmployeeChecksTotal: (state, getters) => {
      const total = Number(state.checksTotal);
      if (Number.isFinite(total) && total >= 0) return total;
      return getters.selectedEmployeeChecks.length;
    },
    selectedCheckDetails: (state, getters) => {
      const selectedRecordId = String(state.selectedCheckRecord?.checkId ?? state.selectedCheckRecord?.id ?? '');
      const selectedCheckId = String(state.selectedCheckId ?? '');
      const records =
        state.selectedCheckRecord && selectedRecordId && selectedRecordId === selectedCheckId
          ? [state.selectedCheckRecord, ...getters.filteredEmployeeAnalyticsRecords]
          : getters.filteredEmployeeAnalyticsRecords;

      return buildCheckDetails(records, state.selectedCheckId);
    },
    availablePointsForFilters: (state) => state.points,
    employeeAnalyticsHasData: (state, getters) => getters.filteredEmployeeAnalyticsRecords.length > 0,
    employeeAnalyticsCurrentRequestKey: (state) => state.recordsRequestKey,
  },
  mutations: {
    SET_RECORDS(state, records) {
      state.records = Array.isArray(records) ? records : [];
      state.recordsLoaded = true;
      state.lastFetchedAt = Date.now();
      state.recordsError = null;
    },
    SET_CHECKS_TOTAL(state, total) {
      const normalized = Number(total);
      state.checksTotal = Number.isFinite(normalized) && normalized >= 0 ? normalized : 0;
    },
    SET_CHECKS_PAGE(state, payload = {}) {
      state.records = Array.isArray(payload?.records) ? payload.records : [];
      state.recordsLoaded = true;
      state.lastFetchedAt = Date.now();
      state.recordsError = null;
      const normalizedTotal = Number(payload?.total);
      state.checksTotal = Number.isFinite(normalizedTotal) && normalizedTotal >= 0 ? normalizedTotal : 0;
    },
    SET_SUMMARY_SNAPSHOT(state, summary) {
      state.summarySnapshot = summary || null;
    },
    SET_RATING_ROWS_SNAPSHOT(state, rows) {
      state.ratingRowsSnapshot = Array.isArray(rows) ? rows : [];
    },
    SET_SUMMARY_CACHE_ENTRY(state, payload = {}) {
      const key = payload?.key;
      if (!key) return;
      state.summaryCache = {
        ...(state.summaryCache || {}),
        [key]: payload.entry || null,
      };
    },
    SET_TOP_CHECK_CACHE_ENTRY(state, payload = {}) {
      const key = payload?.key;
      if (!key) return;
      state.topCheckCache = {
        ...(state.topCheckCache || {}),
        [key]: payload.entry || null,
      };
    },
    SET_TOP_DISH_CACHE_ENTRY(state, payload = {}) {
      const key = payload?.key;
      if (!key) return;
      state.topDishCache = {
        ...(state.topDishCache || {}),
        [key]: payload.entry || null,
      };
    },
    CLEAR_ANALYTICS_CACHE(state) {
      state.summaryCache = {};
      state.topCheckCache = {};
      state.topDishCache = {};
      state.selectedEmployeeAggregateKey = '';
      state.selectedEmployeeAggregateRecords = [];
      state.selectedEmployeeTopCheckKey = '';
      state.selectedEmployeeTopCheck = null;
      state.selectedEmployeeTopDishKey = '';
      state.selectedEmployeeTopDish = null;
    },
    SET_SELECTED_EMPLOYEE_AGGREGATE_RECORDS(state, payload = {}) {
      state.selectedEmployeeAggregateKey = payload?.key || '';
      state.selectedEmployeeAggregateRecords = Array.isArray(payload?.records) ? payload.records : [];
    },
    SET_SELECTED_EMPLOYEE_CHECK_SEARCH_QUERY(state, value = '') {
      state.selectedEmployeeCheckSearchQuery = normalizeCheckSearchQuery(value);
    },
    SET_RECORDS_LOADING(state, value) {
      state.recordsLoading = Boolean(value);
      state.recordsLoadingCount = state.recordsLoading ? Math.max(1, state.recordsLoadingCount || 0) : 0;
    },
    START_RECORDS_LOADING(state, requestId = 0) {
      state.recordsLoadingCount = Math.max(0, Number(state.recordsLoadingCount) || 0) + 1;
      state.recordsLoadingRequestId = Number(requestId) || state.recordsLoadingRequestId;
      state.recordsLoading = true;
    },
    FINISH_RECORDS_LOADING(state, requestId = 0) {
      const normalizedRequestId = Number(requestId) || 0;
      if (normalizedRequestId && state.recordsLoadingRequestId !== normalizedRequestId) {
        state.recordsLoadingCount = Math.max(0, (Number(state.recordsLoadingCount) || 0) - 1);
        return;
      }

      state.recordsLoadingCount = Math.max(0, (Number(state.recordsLoadingCount) || 0) - 1);
      state.recordsLoading = false;
      state.recordsLoadingCount = 0;
    },
    START_SUMMARY_LOADING(state, requestId = 0) {
      state.summaryLoadingRequestId = Number(requestId) || state.summaryLoadingRequestId;
      state.summaryLoading = true;
    },
    FINISH_SUMMARY_LOADING(state, requestId = 0) {
      const normalizedRequestId = Number(requestId) || 0;
      if (normalizedRequestId && state.summaryLoadingRequestId !== normalizedRequestId) return;

      state.summaryLoading = false;
    },
    SET_RECORDS_ERROR(state, error) {
      state.recordsError = error || null;
    },
    SET_FILTERS(state, payload = {}) {
      Object.assign(state, payload);
    },
    SET_SELECTED_POINTS(state, pointIds = []) {
      state.selectedPointIds = Array.isArray(pointIds) ? pointIds : [];
    },
    SET_DATE_PRESET(state, preset) {
      state.datePreset = preset;
    },
    SET_DATE_RANGE(state, range = []) {
      state.dateRange = normalizeDateRangeSeconds(range);
    },
    SET_SEARCH_EMPLOYEE(state, value) {
      state.searchEmployee = value || '';
    },
    SET_EMPLOYEE_SORT(state, value) {
      state.employeeSort = {
        ...state.employeeSort,
        ...(value || {}),
      };
    },
    SET_CHECKS_SORT(state, value) {
      state.checksSort = normalizeChecksSort({
        ...state.checksSort,
        ...(value || {}),
      });
    },
    SET_CHECKS_PAGINATION(state, value) {
      state.checksPagination = {
        ...state.checksPagination,
        ...(value || {}),
      };
    },
    SET_SELECTED_EMPLOYEE_ID(state, employeeId) {
      state.selectedEmployeeId = employeeId || null;
    },
    SET_SELECTED_EMPLOYEE_TOP_CHECK(state, payload = {}) {
      state.selectedEmployeeTopCheckKey = payload?.key || '';
      state.selectedEmployeeTopCheck = payload?.topCheck || null;
    },
    SET_SELECTED_EMPLOYEE_TOP_CHECK_LOADING(state, payload = {}) {
      const key = payload?.key || '';
      const value = Boolean(payload?.value);
      if (value) {
        state.selectedEmployeeTopCheckLoadingKey = key;
        state.selectedEmployeeTopCheckLoading = true;
        return;
      }

      if (!key || state.selectedEmployeeTopCheckLoadingKey === key) {
        state.selectedEmployeeTopCheckLoading = false;
        state.selectedEmployeeTopCheckLoadingKey = '';
      }
    },
    SET_SELECTED_EMPLOYEE_TOP_DISH(state, payload = {}) {
      state.selectedEmployeeTopDishKey = payload?.key || '';
      state.selectedEmployeeTopDish = payload?.topDish || null;
    },
    SET_SELECTED_EMPLOYEE_TOP_DISH_LOADING(state, payload = {}) {
      const key = payload?.key || '';
      const value = Boolean(payload?.value);
      if (value) {
        state.selectedEmployeeTopDishLoadingKey = key;
        state.selectedEmployeeTopDishLoading = true;
        return;
      }

      if (!key || state.selectedEmployeeTopDishLoadingKey === key) {
        state.selectedEmployeeTopDishLoading = false;
        state.selectedEmployeeTopDishLoadingKey = '';
      }
    },
    SET_SELECTED_CHECK_ID(state, checkId) {
      state.selectedCheckId = checkId || null;
    },
    SET_SELECTED_CHECK_RECORD(state, record) {
      state.selectedCheckRecord = record && typeof record === 'object' ? record : null;
    },
    SET_SELECTED_CHECK_ITEMS_LOADING(state, value) {
      state.selectedCheckItemsLoading = Boolean(value);
    },
    SET_SELECTED_CHECK_ITEMS(state, payload = {}) {
      const checkId = payload?.checkId == null ? '' : String(payload.checkId);
      if (!checkId) return;
      const items = Array.isArray(payload.items) ? payload.items : [];
      const next = state.records.map((record) => {
        if (String(record?.checkId ?? '') !== checkId && String(record?.id ?? '') !== checkId) return record;
        return {
          ...record,
          items,
        };
      });
      state.records = next;
      if (String(state.selectedCheckRecord?.checkId ?? state.selectedCheckRecord?.id ?? '') === checkId) {
        state.selectedCheckRecord = {
          ...state.selectedCheckRecord,
          items,
        };
      }
    },
    SET_REFERENCE_DATA(state, payload = {}) {
      state.points = Array.isArray(payload.points) ? payload.points : [];
      state.pointsLoaded = true;
      state.refsMerchantId = payload.merchantId ?? null;
      state.employeesById = payload.employeesById || {};
      state.pointsById = payload.pointsById || {};
      state.reviewsByCheckId = payload.reviewsByCheckId || {};
    },
    SET_RECORDS_REQUEST_KEY(state, key) {
      state.recordsRequestKey = key || '';
    },
    SET_HYDRATED_FROM_ROUTE(state, value) {
      state.hydratedFromRoute = Boolean(value);
    },
    SET_EXPORT_LOADING(state, value) {
      state.exportLoading = Boolean(value);
    },
    RESET_EMPLOYEE_ANALYTICS_STATE(state) {
      Object.assign(state, defaultState());
    },
  },
  actions: {
    async loadReferenceData({ commit, dispatch, rootState, state }) {
      const merchantId = rootState?.login?.merchant?.merchantId || rootState?.login?.merchant?.id || null;
      if (state.pointsLoaded && state.refsMerchantId === merchantId && Object.keys(state.employeesById).length) {
        return {
          points: state.points,
          merchantId: state.refsMerchantId,
          employeesById: state.employeesById,
          pointsById: state.pointsById,
        };
      }

      const [pointsRaw, personalsRaw] = await Promise.all([
        withTimeout(
          Promise.resolve(getPointsName()).catch((error) => {
            console.error('employeeAnalytics:loadReferenceData:points:error', error);
            return [];
          }),
          REFERENCE_DATA_TIMEOUT_MS,
          [],
        ),
        withTimeout(
          Promise.resolve(dispatch('personal/fetchList', undefined, { root: true })).catch((error) => {
            console.error('employeeAnalytics:loadReferenceData:personals:error', error);
            return [];
          }),
          REFERENCE_DATA_TIMEOUT_MS,
          [],
        ),
      ]);
      const points = normalizeReferenceList(pointsRaw).map((point) => normalizePoint(point));
      const employees = normalizeReferenceList(personalsRaw).map((employee) => normalizeEmployee(employee));
      const currentOperator = buildCurrentOperatorEmployee(rootState);
      if (
        currentOperator &&
        !employees.some(
          (employee) =>
            employee.employeeId === currentOperator.employeeId ||
            employee.employeeExternalId === currentOperator.employeeId,
        )
      ) {
        employees.push(currentOperator);
      }
      debugReferenceData({ pointsRaw, personalsRaw, points, employees, currentOperator });
      const maps = buildReferenceMaps({ points, employees });
      const refs = {
        merchantId,
        points,
        employees,
        ...maps,
      };

      commit('SET_REFERENCE_DATA', refs);
      return refs;
    },
    async loadEmployeeChecksFeed({ commit, dispatch, rootState, state }, options = {}) {
      const loadingRequestId = ++recordsLoadSequence;
      commit('START_RECORDS_LOADING', loadingRequestId);
      commit('START_SUMMARY_LOADING', loadingRequestId);
      const forceRefresh = Boolean(options?.forceRefresh);
      let requestKey = '';
      let ownsInflightFeed = false;
      let activeFeedPromise = null;
      let selectedEmployeeId = '';
      try {
        const searchQuery = normalizeCheckSearchQuery(options?.searchQuery);
        commit('SET_SELECTED_EMPLOYEE_CHECK_SEARCH_QUERY', searchQuery);
        const refs = await dispatch('loadReferenceData');
        selectedEmployeeId = resolveEmployeeId(state.selectedEmployeeId, refs?.employeesById);

        const merchantId = rootState?.login?.merchant?.merchantId || rootState?.login?.merchant?.id || 'merchant';
        requestKey = buildRequestKey({
          merchantId,
          employeeId: selectedEmployeeId,
          pointIds: state.selectedPointIds,
          dateRange: state.dateRange,
          checksSort: state.checksSort,
          checksPagination: state.checksPagination,
        });
        debugEmployeeAnalyticsLoad('request:start', {
          requestKey,
          selectedEmployeeId,
          pointIds: [...state.selectedPointIds],
          datePreset: state.datePreset,
          dateRange: [...state.dateRange],
          checksSort: { ...state.checksSort },
          checksPagination: { ...state.checksPagination },
        });
        const currentSummaryKey = buildSummaryKey({
          merchantId,
          pointIds: state.selectedPointIds,
          dateRange: state.dateRange,
          statuses: ['Paid'],
        });
        const employeeAggregateKey = selectedEmployeeId
          ? buildEmployeeAggregateKey({
              merchantId,
              employeeId: selectedEmployeeId,
              pointIds: state.selectedPointIds,
              dateRange: state.dateRange,
            })
          : '';
        const topCheckKey = selectedEmployeeId
          ? buildRequestKey({
              merchantId,
              employeeId: selectedEmployeeId,
              pointIds: state.selectedPointIds,
              dateRange: state.dateRange,
              scope: 'top-check',
            })
          : '';
        const topDishKey = selectedEmployeeId
          ? buildRequestKey({
              merchantId,
              employeeId: selectedEmployeeId,
              pointIds: state.selectedPointIds,
              dateRange: state.dateRange,
              scope: 'top-dish',
            })
          : '';
        const cachedCurrent = state.summaryCache?.[currentSummaryKey];
        const cachedTopCheck = topCheckKey ? state.topCheckCache?.[topCheckKey] : null;
        const cachedTopDish = topDishKey ? state.topDishCache?.[topDishKey] : null;
        const employeeAggregateFresh = Boolean(
          employeeAggregateKey && state.selectedEmployeeAggregateKey === employeeAggregateKey,
        );
        const selectedSummaryRowFresh = Boolean(
          selectedEmployeeId &&
            (hasEmployeeSummaryRow(state.ratingRowsSnapshot, selectedEmployeeId) ||
              hasEmployeeSummaryRow(cachedCurrent?.rows, selectedEmployeeId)),
        );
        const cacheTtlMs = 5 * 60 * 1000;
        const currentFresh = Boolean(
          !forceRefresh &&
          hasSummaryData(cachedCurrent) && cachedCurrent?.fetchedAt && Date.now() - cachedCurrent.fetchedAt < cacheTtlMs,
        );
        const topCheckFresh = Boolean(
          topCheckKey &&
            hasTopCheckData(cachedTopCheck) &&
            cachedTopCheck?.fetchedAt &&
            Date.now() - cachedTopCheck.fetchedAt < cacheTtlMs,
        );
        const topDishFresh = Boolean(
          topDishKey &&
            hasTopDishData(cachedTopDish) &&
            cachedTopDish?.fetchedAt &&
            Date.now() - cachedTopDish.fetchedAt < cacheTtlMs,
        );
        const hasCurrentSnapshot = Boolean(state.summarySnapshot || state.ratingRowsSnapshot.length);
        if (topCheckFresh && state.selectedEmployeeTopCheckKey !== topCheckKey) {
          commit('SET_SELECTED_EMPLOYEE_TOP_CHECK', {
            key: topCheckKey,
            topCheck: cachedTopCheck.topCheck,
          });
        }
        if (topDishFresh && state.selectedEmployeeTopDishKey !== topDishKey) {
          commit('SET_SELECTED_EMPLOYEE_TOP_DISH', {
            key: topDishKey,
            topDish: cachedTopDish.topDish,
          });
        }

        if (
          state.recordsLoaded &&
          state.recordsRequestKey === requestKey &&
          (hasCurrentSnapshot || currentFresh) &&
          (!selectedEmployeeId ||
            (topCheckFresh && topDishFresh && (selectedSummaryRowFresh || employeeAggregateFresh)))
        ) {
          if (!hasCurrentSnapshot && currentFresh) {
            commit('SET_SUMMARY_SNAPSHOT', applySummaryComparison(cachedCurrent.summary, null));
            commit('SET_RATING_ROWS_SNAPSHOT', cachedCurrent.rows);
          }
          return state.records;
        }

        const inflightFeed = forceRefresh ? null : inflightFeedRequests.get(requestKey);
        if (inflightFeed) {
          return await inflightFeed;
        }

        commit('SET_RECORDS_REQUEST_KEY', requestKey);

        const feedPromise = (async () => {
          const prevDateRange = getPreviousAnalogPeriod(state.dateRange);
          const prevSummaryKey = prevDateRange.length
            ? buildSummaryKey({
                merchantId,
                pointIds: state.selectedPointIds,
                dateRange: prevDateRange,
                statuses: ['Paid'],
              })
            : '';

          const now = Date.now();
          const cachedPrev = prevSummaryKey ? state.summaryCache?.[prevSummaryKey] : null;
          const prevFresh = Boolean(
            !forceRefresh &&
              prevSummaryKey &&
              hasSummaryData(cachedPrev) &&
              cachedPrev?.fetchedAt &&
              now - cachedPrev.fetchedAt < cacheTtlMs,
          );

          const summaryRequests = [];
          if (!currentFresh) {
            summaryRequests.push(
              fetchPersonalOrdersSummaryDedup(
                currentSummaryKey,
                {
                  pointId: state.selectedPointIds.length === 1 ? state.selectedPointIds[0] : undefined,
                  dateFrom: toUnixSeconds(state.dateRange?.[0]),
                  dateTo: toUnixSeconds(state.dateRange?.[1]),
                  statuses: ['Paid'],
                  refs,
                },
                { force: forceRefresh },
              ).then((response) => ({ key: currentSummaryKey, response })),
            );
          }
          if (prevSummaryKey && !prevFresh) {
            summaryRequests.push(
              fetchPersonalOrdersSummaryDedup(
                prevSummaryKey,
                {
                  pointId: state.selectedPointIds.length === 1 ? state.selectedPointIds[0] : undefined,
                  dateFrom: toUnixSeconds(prevDateRange?.[0]),
                  dateTo: toUnixSeconds(prevDateRange?.[1]),
                  statuses: ['Paid'],
                  refs,
                },
                { force: forceRefresh },
              ).then((response) => ({ key: prevSummaryKey, response })),
            );
          }
          const topCheckRequest =
            selectedEmployeeId && topCheckKey && !topCheckFresh
              ? (() => {
                  commit('SET_SELECTED_EMPLOYEE_TOP_CHECK_LOADING', { key: topCheckKey, value: true });
                  return fetchEmployeeTopCheckDedup(
                    topCheckKey,
                    {
                      employeeId: selectedEmployeeId,
                      pointId: state.selectedPointIds.length === 1 ? state.selectedPointIds[0] : '',
                      pointIds: state.selectedPointIds,
                      dateRange: state.dateRange,
                    },
                    { force: forceRefresh },
                  )
                    .then((response) => ({ key: topCheckKey, response }))
                    .catch((error) => {
                      console.error('employeeAnalytics:loadEmployeeTopCheck:error', {
                        employeeId: selectedEmployeeId,
                        pointIds: state.selectedPointIds,
                        dateRange: state.dateRange,
                        message: error?.message || String(error),
                      });
                      return null;
                    })
                    .finally(() => {
                      commit('SET_SELECTED_EMPLOYEE_TOP_CHECK_LOADING', { key: topCheckKey, value: false });
                    });
                })()
              : Promise.resolve(null);
          const topDishRequest =
            selectedEmployeeId && topDishKey && !topDishFresh
              ? (() => {
                  commit('SET_SELECTED_EMPLOYEE_TOP_DISH_LOADING', { key: topDishKey, value: true });
                  return fetchEmployeeTopDishDedup(
                    topDishKey,
                    {
                      employeeId: selectedEmployeeId,
                      pointId: state.selectedPointIds.length === 1 ? state.selectedPointIds[0] : '',
                      pointIds: state.selectedPointIds,
                      dateRange: state.dateRange,
                    },
                    { force: forceRefresh },
                  )
                    .then((response) => ({ key: topDishKey, response }))
                    .catch((error) => {
                      console.error('employeeAnalytics:loadEmployeeTopDish:error', {
                        employeeId: selectedEmployeeId,
                        pointIds: state.selectedPointIds,
                        dateRange: state.dateRange,
                        message: error?.message || String(error),
                      });
                      return null;
                    })
                    .finally(() => {
                      commit('SET_SELECTED_EMPLOYEE_TOP_DISH_LOADING', { key: topDishKey, value: false });
                    });
                })()
              : Promise.resolve(null);

          const [checksResponse, fetchedSummaries, fetchedTopCheck, fetchedTopDish] = await Promise.all([
            selectedEmployeeId
              ? getEmployeeChecksFeed({
                  employeeId: selectedEmployeeId,
                  pointId: state.selectedPointIds.length === 1 ? state.selectedPointIds[0] : '',
                  dateRange: state.dateRange,
                  refs,
                  sort: state.checksSort.field,
                  orderBy: state.checksSort.order,
                  page: state.checksPagination.page,
                  rowsPerPage: state.checksPagination.rowsPerPage,
                })
              : Promise.resolve({ records: [], refs }),
            Promise.all(summaryRequests),
            topCheckRequest,
            topDishRequest,
          ]);

          const fetchedSummariesByKey = {};
          for (const item of fetchedSummaries) {
            const normalized = toEmployeeAnalyticsSummary(item.response, checksResponse.refs || refs);
            fetchedSummariesByKey[item.key] = normalized;
            commit('SET_SUMMARY_CACHE_ENTRY', {
              key: item.key,
              entry: {
                summary: normalized.summary,
                rows: normalized.rows,
                fetchedAt: Date.now(),
              },
            });
          }
          if (
            fetchedTopCheck?.key &&
            fetchedTopCheck?.response &&
            Object.prototype.hasOwnProperty.call(fetchedTopCheck.response, 'topCheck')
          ) {
            commit('SET_TOP_CHECK_CACHE_ENTRY', {
              key: fetchedTopCheck.key,
              entry: {
                topCheck: fetchedTopCheck.response.topCheck,
                fetchedAt: Date.now(),
              },
            });
          }
          if (fetchedTopDish?.key && fetchedTopDish?.response?.topDish) {
            commit('SET_TOP_DISH_CACHE_ENTRY', {
              key: fetchedTopDish.key,
              entry: {
                topDish: fetchedTopDish.response.topDish,
                fetchedAt: Date.now(),
              },
            });
          }

          const currentNormalized = currentFresh
            ? { summary: cachedCurrent.summary, rows: cachedCurrent.rows }
            : fetchedSummariesByKey[currentSummaryKey]
              ? fetchedSummariesByKey[currentSummaryKey]
            : state.summaryCache?.[currentSummaryKey]
              ? {
                  summary: state.summaryCache[currentSummaryKey].summary,
                  rows: state.summaryCache[currentSummaryKey].rows,
                }
              : { summary: null, rows: [] };
          const prevNormalized = prevSummaryKey
            ? prevFresh
              ? { summary: cachedPrev.summary }
              : fetchedSummariesByKey[prevSummaryKey]
                ? { summary: fetchedSummariesByKey[prevSummaryKey].summary }
              : state.summaryCache?.[prevSummaryKey]
                ? { summary: state.summaryCache[prevSummaryKey].summary }
                : { summary: null }
            : { summary: null };

          const summaryWithTrend = applySummaryComparison(currentNormalized.summary, prevNormalized.summary);
          let employeeAggregateRecords = employeeAggregateFresh ? state.selectedEmployeeAggregateRecords : null;
          if (selectedEmployeeId && employeeAggregateKey && !employeeAggregateFresh) {
            try {
              const aggregateResponse = await fetchEmployeeAggregateDedup(employeeAggregateKey, {
                employeeId: selectedEmployeeId,
                pointId: state.selectedPointIds.length === 1 ? state.selectedPointIds[0] : '',
                dateRange: state.dateRange,
                refs: checksResponse.refs || refs,
                sort: state.checksSort.field,
                orderBy: state.checksSort.order,
                loadAll: true,
              });
              employeeAggregateRecords = aggregateResponse?.records || [];
            } catch (error) {
              console.error('employeeAnalytics:loadEmployeeAggregate:error', {
                employeeId: selectedEmployeeId,
                pointIds: state.selectedPointIds,
                dateRange: state.dateRange,
                message: error?.message || String(error),
              });
              employeeAggregateRecords = null;
            }
          }

          if (state.recordsRequestKey === requestKey && state.recordsLoadingRequestId === loadingRequestId) {
            debugEmployeeAnalyticsLoad('request:commit', {
              requestKey,
              loadingRequestId,
              recordsCount: Array.isArray(checksResponse.records) ? checksResponse.records.length : 0,
              summary: summaryWithTrend,
              ratingRowsCount: Array.isArray(currentNormalized.rows) ? currentNormalized.rows.length : 0,
              currentSummaryKey,
              prevSummaryKey,
              ratingRowsPreview: Array.isArray(currentNormalized.rows)
                ? currentNormalized.rows.slice(0, 5).map((row) => ({
                    employeeId: row.employeeId,
                    employeeName: row.employeeName,
                    revenue: row.revenue,
                    checksCount: row.checksCount,
                  }))
                : [],
            });
            commit('SET_REFERENCE_DATA', checksResponse.refs);
            commit('SET_RECORDS', checksResponse.records);
            commit('SET_CHECKS_TOTAL', checksResponse.total);
            commit('SET_SUMMARY_SNAPSHOT', summaryWithTrend);
            commit('SET_RATING_ROWS_SNAPSHOT', currentNormalized.rows);
            if (selectedEmployeeId && employeeAggregateKey && Array.isArray(employeeAggregateRecords)) {
              commit('SET_SELECTED_EMPLOYEE_AGGREGATE_RECORDS', {
                key: employeeAggregateKey,
                records: employeeAggregateRecords,
              });
            }
            if (topCheckKey) {
              const topCheck = topCheckFresh
                ? cachedTopCheck.topCheck
                : fetchedTopCheck?.response?.topCheck || state.topCheckCache?.[topCheckKey]?.topCheck || null;
              commit('SET_SELECTED_EMPLOYEE_TOP_CHECK', { key: topCheckKey, topCheck });
            }
            if (topDishKey) {
              const topDish = topDishFresh
                ? cachedTopDish.topDish
                : fetchedTopDish?.response?.topDish || state.topDishCache?.[topDishKey]?.topDish || null;
              commit('SET_SELECTED_EMPLOYEE_TOP_DISH', { key: topDishKey, topDish });
            }
          } else {
            debugEmployeeAnalyticsLoad('request:stale-skip', {
              requestKey,
              currentRequestKey: state.recordsRequestKey,
              loadingRequestId,
              currentLoadingRequestId: state.recordsLoadingRequestId,
            });
          }
          return checksResponse.records;
        })();

        activeFeedPromise = feedPromise;
        inflightFeedRequests.set(requestKey, feedPromise);
        ownsInflightFeed = true;
        return await feedPromise;
      } catch (error) {
        console.error('employeeAnalytics:loadEmployeeChecksFeed:error', {
          requestKey,
          employeeId: selectedEmployeeId,
          pointIds: state.selectedPointIds,
          dateRange: state.dateRange,
          message: error?.message || String(error),
        });
        if (state.recordsRequestKey === requestKey) {
          commit('SET_RECORDS_ERROR', error?.message || String(error));
        }
        throw error;
      } finally {
        if (ownsInflightFeed && inflightFeedRequests.get(requestKey) === activeFeedPromise) {
          inflightFeedRequests.delete(requestKey);
        }
        commit('FINISH_SUMMARY_LOADING', loadingRequestId);
        commit('FINISH_RECORDS_LOADING', loadingRequestId);
      }
    },
    async reloadEmployeeChecksFeed({ commit, dispatch }, options = {}) {
      commit('SET_RECORDS_REQUEST_KEY', '');
      commit('SET_RECORDS_ERROR', null);
      commit('SET_SUMMARY_SNAPSHOT', null);
      commit('SET_RATING_ROWS_SNAPSHOT', []);
      if (options?.forceRefresh) {
        commit('CLEAR_ANALYTICS_CACHE');
      }
      return dispatch('loadEmployeeChecksFeed', options);
    },
    async loadEmployeeChecksPage({ commit, dispatch, rootState, state }, options = {}) {
      const loadingRequestId = ++recordsLoadSequence;
      commit('START_RECORDS_LOADING', loadingRequestId);
      let requestKey = '';
      let ownsInflightFeed = false;
      let selectedEmployeeId = '';
      let ownsSummaryLoading = false;

      try {
        const refs = await dispatch('loadReferenceData');
        selectedEmployeeId = resolveEmployeeId(state.selectedEmployeeId, refs?.employeesById);
        const merchantId = rootState?.login?.merchant?.merchantId || rootState?.login?.merchant?.id || 'merchant';
        const searchQuery = normalizeCheckSearchQuery(options?.searchQuery);
        const previousSearchQuery = normalizeCheckSearchQuery(state.selectedEmployeeCheckSearchQuery);
        if (previousSearchQuery !== searchQuery) {
          commit('START_SUMMARY_LOADING', loadingRequestId);
          ownsSummaryLoading = true;
        }
        commit('SET_SELECTED_EMPLOYEE_CHECK_SEARCH_QUERY', searchQuery);
        const employeeAggregateKey = selectedEmployeeId
          ? buildEmployeeAggregateKey({
              merchantId,
              employeeId: selectedEmployeeId,
              pointIds: state.selectedPointIds,
              dateRange: state.dateRange,
            })
          : '';
        let aggregateRecords =
          employeeAggregateKey && state.selectedEmployeeAggregateKey === employeeAggregateKey
            ? state.selectedEmployeeAggregateRecords
            : [];

        if (searchQuery && selectedEmployeeId && employeeAggregateKey && !aggregateRecords.length) {
          const aggregateResponse = await fetchEmployeeAggregateDedup(employeeAggregateKey, {
            employeeId: selectedEmployeeId,
            pointId: state.selectedPointIds.length === 1 ? state.selectedPointIds[0] : '',
            dateRange: state.dateRange,
            refs,
            sort: state.checksSort.field,
            orderBy: state.checksSort.order,
            loadAll: true,
          });
          aggregateRecords = aggregateResponse?.records || [];
          commit('SET_SELECTED_EMPLOYEE_AGGREGATE_RECORDS', {
            key: employeeAggregateKey,
            records: aggregateRecords,
          });
        }

        const orderIds = searchQuery ? collectOrderIdsByNumber(filterScopedRecords(aggregateRecords, state), searchQuery) : [];
        requestKey = buildRequestKey({
          merchantId,
          employeeId: selectedEmployeeId,
          pointIds: state.selectedPointIds,
          dateRange: state.dateRange,
          checksSort: state.checksSort,
          checksPagination: state.checksPagination,
          orderIds,
          searchQuery,
        });

        if (searchQuery && !orderIds.length) {
          commit('SET_RECORDS_REQUEST_KEY', requestKey);
          commit('SET_REFERENCE_DATA', refs);
          commit('SET_CHECKS_PAGE', { records: [], total: 0 });
          return [];
        }

        if (state.recordsLoaded && state.recordsRequestKey === requestKey) {
          return state.records;
        }

        const inflightFeed = inflightFeedRequests.get(requestKey);
        if (inflightFeed) return await inflightFeed;

        commit('SET_RECORDS_REQUEST_KEY', requestKey);
        const feedPromise = selectedEmployeeId
          ? getEmployeeChecksFeed({
              employeeId: selectedEmployeeId,
              pointId: state.selectedPointIds.length === 1 ? state.selectedPointIds[0] : '',
              dateRange: state.dateRange,
              refs,
              sort: state.checksSort.field,
              orderBy: state.checksSort.order,
              page: state.checksPagination.page,
              rowsPerPage: state.checksPagination.rowsPerPage,
              orderIds,
            })
          : Promise.resolve({ records: [], total: 0, refs });

        inflightFeedRequests.set(requestKey, feedPromise);
        ownsInflightFeed = true;
        const checksResponse = await feedPromise;

        if (state.recordsRequestKey === requestKey) {
          commit('SET_REFERENCE_DATA', checksResponse.refs);
          commit('SET_RECORDS', checksResponse.records);
          commit('SET_CHECKS_TOTAL', checksResponse.total);
        }

        return checksResponse.records;
      } catch (error) {
        console.error('employeeAnalytics:loadEmployeeChecksPage:error', {
          requestKey,
          employeeId: selectedEmployeeId,
          pointIds: state.selectedPointIds,
          dateRange: state.dateRange,
          checksPagination: state.checksPagination,
          message: error?.message || String(error),
        });
        if (state.recordsRequestKey === requestKey) {
          commit('SET_RECORDS_ERROR', error?.message || String(error));
        }
        throw error;
      } finally {
        if (ownsInflightFeed) {
          inflightFeedRequests.delete(requestKey);
        }
        if (ownsSummaryLoading) {
          commit('FINISH_SUMMARY_LOADING', loadingRequestId);
        }
        commit('FINISH_RECORDS_LOADING', loadingRequestId);
      }
    },
    async loadEmployeeChecksExport({ dispatch, rootState, state }) {
      const refs = await dispatch('loadReferenceData');
      const selectedEmployeeId = resolveEmployeeId(state.selectedEmployeeId, refs?.employeesById);
      const merchantId = rootState?.login?.merchant?.merchantId || rootState?.login?.merchant?.id || 'merchant';
      const requestKey = buildRequestKey({
        merchantId,
        employeeId: selectedEmployeeId,
        pointIds: state.selectedPointIds,
        dateRange: state.dateRange,
        scope: 'paid-dine-in-export',
        checksSort: state.checksSort,
      });
      const inflightFeed = inflightFeedRequests.get(requestKey);
      if (inflightFeed) return inflightFeed;

      const promise = selectedEmployeeId
        ? getEmployeeChecksFeed({
            employeeId: selectedEmployeeId,
            pointId: state.selectedPointIds.length === 1 ? state.selectedPointIds[0] : '',
            dateRange: state.dateRange,
            refs,
            sort: state.checksSort.field,
            orderBy: state.checksSort.order,
            loadAll: true,
          }).then((response) => response.records || [])
        : Promise.resolve([]);

      inflightFeedRequests.set(requestKey, promise);
      try {
        return await promise;
      } finally {
        inflightFeedRequests.delete(requestKey);
      }
    },
    setPresetAndSyncRange({ commit }, preset = '7d') {
      commit('SET_DATE_PRESET', preset);
      commit('SET_DATE_RANGE', getPresetRange(preset));
    },
    setCustomRange({ commit }, range = []) {
      commit('SET_DATE_PRESET', 'custom');
      commit('SET_DATE_RANGE', range);
    },
    selectEmployee({ commit }, employeeId) {
      commit('SET_SELECTED_EMPLOYEE_ID', employeeId);
    },
    selectCheck({ commit }, checkId) {
      commit('SET_SELECTED_CHECK_ID', checkId);
    },
    selectCheckRecord({ commit }, record) {
      if (!record?.checkId && !record?.id) return;
      commit('SET_SELECTED_CHECK_ID', record.checkId || record.id);
      commit('SET_SELECTED_CHECK_RECORD', record);
    },
    async loadSelectedCheckItems({ commit, state }) {
      const checkId = state.selectedCheckId == null ? '' : String(state.selectedCheckId);
      if (!checkId) return [];

      commit('SET_SELECTED_CHECK_ITEMS_LOADING', true);
      try {
        const record =
          state.records.find((item) => String(item?.checkId ?? '') === checkId || String(item?.id ?? '') === checkId) ||
          (String(state.selectedCheckRecord?.checkId ?? state.selectedCheckRecord?.id ?? '') === checkId
            ? state.selectedCheckRecord
            : null) ||
          null;
        const hasRawOrderItems = Array.isArray(record?.raw?.orderItems) && record.raw.orderItems.length;
        let items = [];
        if (hasRawOrderItems) {
          const orderItems = await enrichOrderItemsWithGoodsDetails({
            orderItems: record.raw.orderItems,
            pointId: record?.pointId || (state.selectedPointIds.length === 1 ? state.selectedPointIds[0] : ''),
          });
          items = normalizeOrderItemsPayload(
            {
              orderItems,
              discounts: record.raw.discounts,
            },
            { includeModifiersAsLines: true },
          );
        } else if (record?.items?.length) {
          items = record.items;
        } else {
          items = normalizeOrderItemsPayload({
            orderItems: record?.raw?.orderItems,
            discounts: record?.raw?.discounts,
          });
        }
        if (items.length) {
          commit('SET_SELECTED_CHECK_ITEMS', { checkId, items });
          return items;
        }

        const response = await getEmployeeOrderItems({
          personalId: record?.employeeId || state.selectedEmployeeId,
          orderId: checkId,
          pointId: record?.pointId || (state.selectedPointIds.length === 1 ? state.selectedPointIds[0] : ''),
          dateRange: state.dateRange,
        });
        const fetchedOrderItems = await enrichOrderItemsWithGoodsDetails({
          orderItems: response?.orderItems,
          pointId: record?.pointId || (state.selectedPointIds.length === 1 ? state.selectedPointIds[0] : ''),
        });
        const fetchedItems = normalizeOrderItemsPayload(
          {
            ...response,
            orderItems: fetchedOrderItems,
          },
          { includeModifiersAsLines: true },
        );
        if (!record && !state.selectedCheckRecord) {
          commit('SET_SELECTED_CHECK_RECORD', {
            checkId,
            id: checkId,
            employeeId: state.selectedEmployeeId,
            status: 'Paid',
            serviceType: 'dine-in',
          });
        }
        commit('SET_SELECTED_CHECK_ITEMS', { checkId, items: fetchedItems });
        return fetchedItems;
      } finally {
        commit('SET_SELECTED_CHECK_ITEMS_LOADING', false);
      }
    },
    hydrateFromRouteQuery({ commit, dispatch }, query = {}) {
      const pointIds = typeof query.pointIds === 'string' && query.pointIds.length ? query.pointIds.split(',') : [];
      const preset = typeof query.preset === 'string' ? query.preset : '7d';
      const dateFrom = query.dateFrom ? Number(query.dateFrom) : null;
      const dateTo = query.dateTo ? Number(query.dateTo) : null;
      const sortField = typeof query.sortField === 'string' ? query.sortField : null;
      const sortOrder = typeof query.sortOrder === 'string' ? query.sortOrder : null;
      const checkSortField = typeof query.sort === 'string' ? query.sort : null;
      const checkSortOrder = typeof query.orderBy === 'string' ? query.orderBy : null;
      const page = query.page ? Number(query.page) : null;
      const rowsPerPage = query.rowsPerPage ? Number(query.rowsPerPage) : null;

      commit('SET_SELECTED_POINTS', pointIds);
      commit('SET_SEARCH_EMPLOYEE', typeof query.search === 'string' ? query.search : '');
      if (sortField || sortOrder) {
        commit('SET_EMPLOYEE_SORT', { field: sortField || undefined, order: sortOrder || undefined });
      }

      if (checkSortField || checkSortOrder) {
        commit('SET_CHECKS_SORT', { field: checkSortField || undefined, order: checkSortOrder || undefined });
      }

      commit('SET_CHECKS_PAGINATION', {
        page: page || 1,
        ...(rowsPerPage ? { rowsPerPage } : {}),
      });

      if (dateFrom && dateTo) {
        dispatch('setCustomRange', [dateFrom, dateTo]);
      } else {
        dispatch('setPresetAndSyncRange', preset);
      }

      commit('SET_HYDRATED_FROM_ROUTE', true);
    },
    resetFilters({ commit, dispatch }) {
      commit('SET_SELECTED_POINTS', []);
      commit('SET_SEARCH_EMPLOYEE', '');
      commit('SET_EMPLOYEE_SORT', { field: 'revenue', order: 'desc' });
      commit('SET_CHECKS_SORT', DEFAULT_CHECKS_SORT);
      commit('SET_CHECKS_PAGINATION', { page: 1, rowsPerPage: 10 });
      dispatch('setPresetAndSyncRange', '7d');
    },
  },
};
